import { ReactElement, useState } from 'react'
import { Delete } from '@mui/icons-material'
import { Box, CircularProgress, Collapse, IconButton, Stack } from '@mui/material'
import { DOC_ICON } from '@editor/components/DocumentFileRow/utils'
import ConvertButton from '@editor/pages/Plans/components/PlansSection/components/ConvertButton'
import { MaterialSymbol } from '@ui2/icons'
import { EditableFileListTitle, FileListRow } from '@ui2/structure'
import { useSnackbar } from '@ui/snackbar'
import { useModelStore } from '@editorStores'
import { usePlanPdfViewer } from '../../PlanPdfViewerContext'
import downloadPlanPdf from '../../downloadPlanPdf'
import useDeletePlan from '../../hooks/useDeletePlan'
import useUpdatePlanMetadata from '../../hooks/useUpdatePlanMetadata'
import PlanInformationForm from '../PlanInformationForm'
import PlanPdfChangedNotice from '../PlanPdfChangedNotice'
import PlanTargetViewerChip from '../PlanTargetViewerChip'

interface Props {
  plan: Plan
  selected?: boolean
  onClick?: () => void
}

/**
 * Saved plan styled like the document rows of Results2's Dokumente tab: PDF icon,
 * click-to-rename title (once selected, like selectable document rows), the PDF viewer the plan
 * was optimised for, rename, download, PDF/A conversion and delete. The selected plan expands its Kommentar/Tags form below the row.
 */
const PlanFileRow = ({ plan, selected, onClick }: Props): ReactElement => {
  const meta = DOC_ICON.pdf
  const { enqueueSnackbar } = useSnackbar()
  const { viewer } = usePlanPdfViewer()
  const projectName = useModelStore(state => state.project?.name)
  const { updatePlanMetadata, isUpdating: isRenaming } = useUpdatePlanMetadata()
  const { mutate: deletePlan, isLoading: isDeleting } = useDeletePlan(plan)
  const [isEditing, setIsEditing] = useState(false)

  return (
    <>
      <FileListRow
        selected={selected}
        onClick={onClick}
        icon={<MaterialSymbol icon={meta.icon} size={22} sx={{ color: meta.color }} />}
        title={
          <EditableFileListTitle
            name={plan.node_name}
            isEditing={isEditing}
            onEditingChange={setIsEditing}
            onRename={name => updatePlanMetadata(plan, { name })}
            canRenameByClick={!onClick || !!selected}
          />
        }
        subtitle="PDF"
        // Creation date and time, e.g. "PDF · 05.10.2026, 13:29".
        timestamp={plan.date ?? undefined}
        badge={
          plan.target_viewer ? (
            <PlanTargetViewerChip targetViewer={plan.target_viewer} />
          ) : undefined
        }
        actions={
          <>
            {!isEditing &&
              (isRenaming ? (
                <Box p={0.5} display="flex">
                  <CircularProgress size={16} />
                </Box>
              ) : (
                <IconButton
                  size="small"
                  title="Umbenennen"
                  onClick={event => {
                    event.stopPropagation()
                    setIsEditing(true)
                  }}
                >
                  <MaterialSymbol icon="edit" size={16} />
                </IconButton>
              ))}
            <IconButton
              size="small"
              disabled={!plan.url}
              title="Herunterladen"
              onClick={async event => {
                event.stopPropagation()
                try {
                  await downloadPlanPdf(plan, projectName)
                } catch {
                  enqueueSnackbar('Fehler beim Herunterladen des Plans', { variant: 'error' })
                }
              }}
            >
              <MaterialSymbol icon="download" size={16} />
            </IconButton>
            {/* The conversion dialog is portaled, but its clicks still bubble to the row. */}
            <Box display="flex" onClick={event => event.stopPropagation()}>
              <ConvertButton
                plan={plan}
                disabled={!plan.url}
                getBlobPDFA={async (url, conformance) =>
                  viewer ? viewer.getBlobPDFA(url, conformance) : null
                }
              />
            </Box>
            <IconButton
              size="small"
              disabled={isDeleting}
              title="Löschen"
              onClick={event => {
                event.stopPropagation()
                deletePlan()
              }}
              data-cy="plan-view-delete-plan-button"
            >
              {isDeleting ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <Delete fontSize="inherit" />
              )}
            </IconButton>
          </>
        }
      />
      {/* The selected plan's panel, expanded right below its row: unsaved PDF changes, Kommentar
          and Tags. */}
      <Collapse in={!!selected} unmountOnExit>
        <Box
          sx={{
            px: 1,
            pt: 1.5,
            pb: 1,
            borderBottom: theme => `1px solid ${theme.palette.divider}`,
          }}
        >
          <Stack spacing={1.5}>
            <PlanPdfChangedNotice plan={plan} />
            <PlanInformationForm plan={plan} />
          </Stack>
        </Box>
      </Collapse>
    </>
  )
}

export default PlanFileRow
